import React, { useEffect, useState } from "react";
import axiosAuth from "../Axios/Interceptors";
import AppContext from "../Context/Context";
import { useGlobalState } from "../Context/GlobalStateContext";
import LoadingAnimation from "../LoadingAnimation/LoadingAnimation";
import { showErrorMessage, showSuccessMessage } from "../Shared/Toast";

interface Props {
  QuoteID: number;
  MNVJobID: number;
  LineItemType: number;
  VAT: number;
  PreFillData?: any;
  ClosePopupAndLoadLineItem: (value: string) => void;
}

const MNVLineItems: React.FC<Props> = ({
  QuoteID,
  MNVJobID,
  LineItemType,
  PreFillData,
  VAT,
  ClosePopupAndLoadLineItem,
}) => {
  const [data, setData] = useState<any>({});
  const [ShowErrors, setShowErrors] = useState<string[]>(["", ""]);
  const globalState = useGlobalState();
  const [isAnimation, setIsAnimation] = useState(false);
  // ----------------------------
  // CLEAR LINE ITEM (original logic)
  // ----------------------------
  const ClearLineItem = () => {
    const defaultType = LineItemType === 0 ? "2" : "1";
    
    setData({
      Type: defaultType,
      Amount: 0,
      AmountWithVat: 0,
      MarkPaid: false,
      HideOnInvoice: false,
      Note: "",
      RefID: "",
      Title: "Service",
      PTPaymentInvoiceLineItemID: 0,
    });

    setShowErrors(["", ""]);
  };

  useEffect(() => {
    ClearLineItem();
    if (PreFillData) {
      let amountWithVat = 0;
      if (PreFillData.ObjectType == 5) {
        PreFillData.Amount = PreFillData.Amount / 1.2;
      }
      amountWithVat = PreFillData.Amount + (PreFillData.Amount * VAT) / 100;

      setData((prev: any) => ({
        ...prev,
        Type: PreFillData.Type,
        Title: PreFillData.PTPaymentInvoiceLineItemID == 0 ? "Service" : PreFillData.Title,
        RefID: PreFillData.RefID,
        Amount: PreFillData.Amount.toFixed(2),
        AmountWithVAT: amountWithVat.toFixed(2),
        Note: PreFillData.Note ? PreFillData.Note : "",
        HideOnInvoice: PreFillData.HideOnInvoice,
        PTPaymentInvoiceLineItemID: PreFillData.PTPaymentInvoiceLineItemID,
      }));
    }
  }, [PreFillData, LineItemType]);

  const SaveLineItem = () => {
    const errors = ["", ""];
    let valid = true;

    if (data.Amount <= 0) {
      errors[1] = "Enter amount greater than 0.";
      valid = false;
    }
    if (!data.RefID || data.RefID.trim() === "") {
      errors[0] = "Required.";
      valid = false;
    }
    if(!data.Title || data.Title.trim() === ""){
errors[2] = "Required.";
      valid = false;
    }

    setShowErrors(errors);

    if (valid) AjaxSaveLineItem();
  };

  // ----------------------------
  // AJAX SAVE (original logic copied exactly)
  // ----------------------------
  const AjaxSaveLineItem = () => {
    const payload: any = {
      PTPaymentInvoiceLineItemID: data.PTPaymentInvoiceLineItemID,
      Type: data.Type,
      Amount: data.Amount,
      Title: data.Title,
      Note: data.Note === "" ? data.RefID : data.Note,
      RefID: data.RefID,
      QuoteID: QuoteID,
      MNVJobID: MNVJobID,
      LineItemType: LineItemType,
      MarkPaid: data.MarkPaid,
      HideOnInvoice: data.HideOnInvoice,
    };
    if (data.Type == "4" || data.Type == "2") {
      payload.Amount = data.AmountWithVAT;
    }

    setIsAnimation(true);

    axiosAuth
      .post(AppContext.APIURL + "api/MNVQuoteAdmin/AjaxSaveLineItem", payload)
      .then((res) => {
        setIsAnimation(false);

        const StatusData = res.data[0][0];

        if (StatusData.Status === 1) {
          ClosePopupAndLoadLineItem("OK");
          showSuccessMessage("Line item has been added successfully");
        } else {
          showErrorMessage(StatusData.Message);
        }
      })
      .catch(() => {
        setIsAnimation(false);
      });
  };

  // ----------------------------
  // GENERIC UPDATE HANDLER
  // ----------------------------
  const updateField = (field: string, value: any) => {
    if (field == "AmountWithVAT") {
      let amount = 0;

      amount = value / (1 + VAT / 100);

      setData((prev: any) => ({
        ...prev,
        AmountWithVAT: value,
        Amount: amount.toFixed(2),
      }));
    } else if (field == "Amount") {
      let amountWithVat = 0;
      amountWithVat = value + (value * VAT) / 100;
      setData((prev: any) => ({
        ...prev,
        Amount: value,
        AmountWithVAT: amountWithVat.toFixed(2),
      }));
    } else {
      setData((prev: any) => ({ ...prev, [field]: value }));
    }
  };

  return (
    <div className="grid-d fr-1 gap-20 mt-20">
      {isAnimation && <LoadingAnimation />}
      {/* TYPE DROPDOWN */}
      {data.PTPaymentInvoiceLineItemID == 0 && (
        <div className="field-input grid-d flex-gap3">
          <span className="font-12 text-left label-color">Line Item Type</span>

          <select
            className="input-field w-100"
            value={data.Type}
            onChange={(e) => updateField("Type", e.target.value)}
          >
            {LineItemType === 1 && (
              <>
                <option value="1">
                  Credit (Quote Price, Other Charges, Extra Charges etc)
                </option>
                <option value="100">
                  Credit NO VAT (Quote Price, Other Charges, Extra Charges etc)
                </option>
                <option value="3">
                  Debit (Write off, Refund Adjustment, Discount etc)
                </option>
                <option value="4">Cancellation / Manual Refund etc</option>
              </>
            )}
            <option value="2">Debit (Cash Payment, Bank Deposit etc)</option>
          </select>
        </div>
      )}

      <div className="field-input grid-d flex-gap3">
        <span className="font-12 text-left label-color">
          Line Item Category
        </span>

        <select
          className="input-field w-100"
          value={data.Title}
          onChange={(e) => updateField("Title", e.target.value)}
        >
          <option value="">Please select</option>
          <option value="Service">Service (all moving fees)</option>
          <option value="Disposal">Disposal services (disposal)</option>
          <option value="Additional Liability">
            Additional Liability (insurance)
          </option>
          <option value="Storage">Storage (storage costs)</option>
          <option value="Materials">Materials (our boxes, tape etc)</option>
          <option value="Charges">Charges (parking, PCN, Fine)</option>
          <option value="Boxes">Boxes (online shop)</option>
        </select>
        
        <span className="font-10 text-left c-red">{ShowErrors[2]}</span>
      </div>

      {/* TITLE/REF */}
      <div className="field-input grid-d flex-gap3">
        <span className="font-12 text-left label-color">Title/Ref#</span>
        <input
          type="text"
          className="input-field w-100"
          value={data.RefID}
          onChange={(e) => updateField("RefID", e.target.value)}
        />
        <span className="font-10 text-left c-red">{ShowErrors[0]}</span>
      </div>

      {/* AMOUNT */}
      <div className="field-input grid-d flex-gap3">
        <span className="font-12 text-left label-color">Amount</span>
        <input
          type="number"
          className="input-field w-100"
          value={data.Amount}
          onChange={(e) => updateField("Amount", Number(e.target.value))}
        />
        <span className="font-10 text-left c-red">{ShowErrors[1]}</span>
      </div>

      {/* AMOUNT With Vat */}
      <div className="field-input grid-d flex-gap3">
        <span className="font-12 text-left label-color">Amount With VAT</span>
        <input
          type="number"
          className="input-field w-100"
          value={data.AmountWithVAT}
          onChange={(e) => updateField("AmountWithVAT", Number(e.target.value))}
        />
        <span className="font-10 text-left c-red">{ShowErrors[1]}</span>
      </div>

      {/* NOTE */}
      <div className="field-input grid-d flex-gap3">
        <span className="font-12 text-left label-color">Note (Optional)</span>
        <textarea
          className="input-field w-100"
          value={data.Note}
          onChange={(e) => updateField("Note", e.target.value)}
        />
      </div>

      {/* HIDE ON INVOICE */}
      {data.Type == "1" && (
        <div className="field-input grid-d flex-gap3">
          <span className="font-12 text-left label-color">
            Do Not Itemise On Invoice PDF
          </span>
          <div className="flex-d">
            <input
              type="checkbox"
              checked={data.HideOnInvoice}
              onChange={(e) => updateField("HideOnInvoice", e.target.checked)}
            />
          </div>
        </div>
      )}

      {/* BUTTONS */}
      <div className="flex-daj gap-10 mt-20">
        <div className="flex-daj btn-start flex-gap10">
          {data.PTPaymentInvoiceLineItemID &&
          data.PTPaymentInvoiceLineItemID > 0 ? (
            <a className="btn-primary flex-daj" onClick={SaveLineItem}>
              Update
            </a>
          ) : (
            <a className="btn-primary flex-daj" onClick={SaveLineItem}>
              Save
            </a>
          )}

          {data.PTPaymentInvoiceLineItemID == 0 && (
            <a className="btn-primary flex-daj" onClick={ClearLineItem}>
              Clear
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default MNVLineItems;
